// src/components/Chat/shared/MessageActions.tsx
// Menu de ações no hover da mensagem (Reply, Delete, Copy, Info)

import { motion, AnimatePresence } from "framer-motion";
import { Reply, Trash2, Copy, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ChatMessage } from "@/types/chat";

interface MessageActionsProps {
  message: ChatMessage;
  isVisible: boolean;
  isOwnMessage: boolean;
  canReply?: boolean; // false se a mensagem já é um reply
  onReply: (message: ChatMessage) => void;
  onDelete: (message: ChatMessage) => void;
  onCopy: (message: ChatMessage) => void;
  onInfo?: (message: ChatMessage) => void;
  showInfo?: boolean;
}

// Tempo máximo para deletar mensagem (2 horas em milissegundos)
const MAX_DELETE_TIME_MS = 2 * 60 * 60 * 1000;

export function MessageActions({
  message,
  isVisible,
  isOwnMessage,
  canReply = true,
  onReply,
  onDelete,
  onCopy,
  onInfo,
  showInfo = false,
}: MessageActionsProps) {
  const { t } = useTranslation();

  // Não mostrar ações em mensagens deletadas
  if (message.deleted) return null;

  // Verificar se a mensagem pode ser deletada (menos de 2 horas)
  const canDelete = (): boolean => {
    if (!isOwnMessage) return false;

    const messageDate = new Date(message.created_at);
    const now = new Date();
    const diffMs = now.getTime() - messageDate.getTime();

    return diffMs <= MAX_DELETE_TIME_MS;
  };

  const handleCopy = () => {
    if (message.content) {
      navigator.clipboard.writeText(message.content);
    }
    onCopy(message);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className={`absolute -bottom-6 translate-y-full z-20 flex items-center gap-0.5 bg-white rounded-lg shadow-md border border-gray-200 p-0.5 ${
            isOwnMessage ? "right-0" : "left-0"
          }`}
        >
          {/* Botão Reply */}
          {canReply && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onReply(message)}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-blue-600 transition-colors"
              title={t("chatFeatures.actions.reply")}
            >
              <Reply size={14} />
            </motion.button>
          )}

          {/* Botão Copy */}
          {message.content && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCopy}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-colors"
              title={t("chatFeatures.actions.copy")}
            >
              <Copy size={14} />
            </motion.button>
          )}

          {/* Botão Info (apenas se showInfo e onInfo existir) */}
          {showInfo && onInfo && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onInfo(message)}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-colors"
              title={t("chatFeatures.actions.info")}
            >
              <Info size={14} />
            </motion.button>
          )}

          {/* Botão Delete (apenas para mensagens próprias e com menos de 2 horas) */}
          {canDelete() && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onDelete(message)}
              className="p-1.5 rounded-md hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors"
              title={t("chatFeatures.actions.delete")}
            >
              <Trash2 size={14} />
            </motion.button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default MessageActions;
